import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type CanvasItem = {
  id: number;
  type: 'text' | 'image' | string;
  content: string;
  category?: string;
};

type MatrixState = {
  xMin: string;
  xMax: string;
  yMin: string;
  yMax: string;
  qTL: string; // top-left
  qTR: string; // top-right
  qBL: string; // bottom-left
  qBR: string; // bottom-right
};

type NodeState = {
  id: number;
  x: number;
  y: number;
  width: number;
  height: number;
  body: string; // HTML
  category?: string;
  kind?: 'note' | 'matrix' | 'table';
  matrix?: MatrixState;
  table?: { rows: number; cols: number; data: string[][] };
  includedItemIds?: number[]; // scratchpad item ids already merged into this node (for text notes)
};

type ViewState = {
  scale: number;
  offset: {
    x: number;
    y: number;
  };
};

interface InfiniteCanvasModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: CanvasItem[];
  onAddCategory?: (name: string) => void;
  onCategoriesChange?: (names: string[]) => void;
}

declare global {
  interface Window {
    __canvasPendingAdds?: Array<{ id: number; category: string; content: string }>;
    __canvasTables?: Array<{ id: number; rows: number; cols: number; label: string; rowTitles: string[]; colTitles: string[] }>;
  }
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const DEFAULT_NODE_WIDTH = 280;
const DEFAULT_NODE_HEIGHT = 160;
const MIN_NODE_WIDTH = 180;
const MIN_NODE_HEIGHT = 120;

const InfiniteCanvasModal: React.FC<InfiniteCanvasModalProps> = ({ isOpen, onClose, items, onAddCategory, onCategoriesChange }) => {
  const viewportRef = useRef<HTMLDivElement | null>(null);

  // Pan/zoom state
  const [view, setView] = useState<ViewState>({ scale: 1, offset: { x: 0, y: 0 } });
  const scale = view.scale;
  const offset = view.offset;

  // Minimize state
  const [isMinimized, setIsMinimized] = useState(false);

  // Templates menu
  const [showTemplates, setShowTemplates] = useState(false);

  // Node editing state (body only)
  const [editingBodyId, setEditingBodyId] = useState<number | null>(null);

  // Targeting mode content (selected text to paste)
  const [targetingContent, setTargetingContent] = useState<string | null>(null);

  useEffect(() => {
    const onStart = (e: Event) => {
      const detail = (e as CustomEvent<{ content: string }>).detail;
      if (detail && typeof detail.content === 'string') {
        setTargetingContent(detail.content);
      }
    };
    window.addEventListener('canvas-targeting-start', onStart as EventListener);
    return () => window.removeEventListener('canvas-targeting-start', onStart as EventListener);
  }, []);

  // Map incoming items to initial nodes, grouped by category (one node per category)
  const initialNodes = useMemo<NodeState[]>(() => {
    const textItems = items.filter((i) => i.type === 'text' && i.content);
    const byCat = new Map<string, { displayCat: string; itemIds: number[]; html: string }>();
    const toHtml = (txt: string) => txt
      .split('\n')
      .map((line) => `<p>${line.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' } as any)[c] || c)}</p>`) 
      .join('');
    for (const it of textItems) {
      const rawCat = (it.category || 'Ungrouped');
      const key = rawCat.toLowerCase();
      const entry = byCat.get(key) || { displayCat: rawCat, itemIds: [], html: '' };
      entry.itemIds.push(it.id);
      entry.html += toHtml(String(it.content || ''));
      byCat.set(key, entry);
    }
    const colWidth = DEFAULT_NODE_WIDTH + 40;
    const rowHeight = DEFAULT_NODE_HEIGHT + 40;
    return Array.from(byCat.values()).map((entry, idx) => {
      const col = idx % 3;
      const row = Math.floor(idx / 3);
      return {
        id: Date.now() + idx,
        x: col * colWidth,
        y: row * rowHeight,
        width: DEFAULT_NODE_WIDTH,
        height: DEFAULT_NODE_HEIGHT,
        body: entry.html,
        category: entry.displayCat,
        includedItemIds: entry.itemIds,
      } as NodeState;
    });
  }, [items]);

  const [nodes, setNodes] = useState<NodeState[]>(initialNodes);

  // On open, reset minimized and baseline view; keep prior nodes if any
  useEffect(() => {
    if (isOpen) setIsMinimized(false);
  }, [isOpen]);

  // Emit categories whenever nodes change
  useEffect(() => {
    const uniqueCats = Array.from(new Set(nodes.map((n) => n.category).filter((c): c is string => !!c)));
    onCategoriesChange && onCategoriesChange(uniqueCats);
  }, [nodes, onCategoriesChange]);

  // Publish table metadata for external pickers
  useEffect(() => {
    const tables = nodes
      .filter((n) => n.kind === 'table' && n.table)
      .map((n, idx) => {
        const rows = n.table!.rows;
        const cols = n.table!.cols;
        const rowTitles = Array.from({ length: rows }, (_, r) => (n.table!.data[r]?.[0] || `R${r+1}`));
        const colTitles = Array.from({ length: cols }, (_, c) => (n.table!.data[0]?.[c] || `C${c+1}`));
        return { id: n.id, rows, cols, label: `Table #${idx + 1}`, rowTitles, colTitles };
      });
    window.__canvasTables = tables;
    window.dispatchEvent(new CustomEvent('canvas-tables-changed', { detail: tables }));
  }, [nodes]);

  // Incremental sync: append new items to existing category node; create node if category absent
  useEffect(() => {
    setNodes((prev) => {
      const next = [...prev];
      const catIndex = new Map<string, number>();
      next.forEach((n, idx) => {
        const key = (n.category || 'Ungrouped').toLowerCase();
        if (!catIndex.has(key)) catIndex.set(key, idx);
      });
      const alreadyIncluded = new Set<number>();
      next.forEach((n) => (n.includedItemIds || []).forEach((id) => alreadyIncluded.add(id)));
      const toProcess = items.filter((i) => i.type === 'text' && i.content && !alreadyIncluded.has(i.id));
      if (toProcess.length === 0) return prev;
      const toHtml = (txt: string) => txt
        .split('\n')
        .map((line) => `<p>${line.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' } as any)[c] || c)}</p>`) 
        .join('');
      const stackOffset = 28;
      toProcess.forEach((it, idxNew) => {
        const key = (it.category || 'Ungrouped').toLowerCase();
        const html = toHtml(String(it.content || ''));
        const idx = catIndex.get(key);
        if (idx !== undefined) {
          const n = next[idx];
          next[idx] = { ...n, body: (n.body || '') + html, includedItemIds: [...(n.includedItemIds || []), it.id] };
        } else {
          const indexCount = next.length;
          const startX = 0;
          const startY = 0;
          const node: NodeState = {
            id: Date.now() + idxNew,
            x: startX + (indexCount * stackOffset),
            y: startY + (indexCount * stackOffset),
            width: DEFAULT_NODE_WIDTH,
            height: DEFAULT_NODE_HEIGHT,
            body: html,
            category: it.category || 'Ungrouped',
            includedItemIds: [it.id],
          };
          catIndex.set(key, next.length);
          next.push(node);
        }
      });
      return next;
    });
  }, [items]);

  // Listen for external canvas-add events and process any queued adds
  useEffect(() => {
    const processAdd = (payload: { id: number; category: string; content: string }) => {
      if (!payload || !payload.category) return;
      const cat = String(payload.category);
      const asHtml = String(payload.content || '')
        .split('\n')
        .map((line) => `<p>${line.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' } as any)[c] || c)}</p>`) 
        .join('');
      setNodes((prev) => {
        const idx = prev.findIndex((n) => (n.category || '').toLowerCase() === cat.toLowerCase());
        if (idx === -1) return prev; // category not on canvas currently
        const next = prev.slice();
        next[idx] = { ...next[idx], body: (next[idx].body || '') + asHtml };
        return next;
      });
    };

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ id: number; category: string; content: string }>).detail;
      if (detail) processAdd(detail);
    };
    window.addEventListener('canvas-add', handler as EventListener);

    // Drain any queued adds from window on mount
    const queue = window.__canvasPendingAdds || [];
    if (queue.length) {
      const pending = queue.splice(0, queue.length);
      pending.forEach(processAdd);
    }

    return () => {
      window.removeEventListener('canvas-add', handler as EventListener);
    };
  }, []);

  // Listen for table fill requests
  useEffect(() => {
    const onFill = (e: Event) => {
      const detail = (e as CustomEvent<{ nodeId: number; row: number; col: number; content: string }>).detail;
      if (!detail) return;
      const { nodeId, row, col, content } = detail;
      setNodes((prev) => prev.map((n) => {
        if (n.id !== nodeId || n.kind !== 'table' || !n.table) return n;
        const rows = n.table.rows;
        const cols = n.table.cols;
        if (row < 0 || row >= rows || col < 0 || col >= cols) return n;
        const nextData = n.table.data.map((r, ri) => r.map((v, ci) => (ri === row && ci === col ? String(content) : v)));
        return { ...n, table: { rows, cols, data: nextData } };
      }));
    };
    window.addEventListener('canvas-table-fill', onFill as EventListener);
    return () => window.removeEventListener('canvas-table-fill', onFill as EventListener);
  }, []);

  const toWorld = useCallback(
    (clientX: number, clientY: number) => {
      const rect = viewportRef.current?.getBoundingClientRect();
      const x = (clientX - (rect?.left || 0) - offset.x) / scale;
      const y = (clientY - (rect?.top || 0) - offset.y) / scale;
      return { x, y };
    },
    [offset, scale]
  );

  // Background drag to pan
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const panOriginRef = useRef({ x: 0, y: 0 });

  const onBackgroundPointerDown: React.PointerEventHandler<HTMLDivElement> = (e) => {
    // Do not initiate pan if the target is inside a node
    const tgt = e.target as HTMLElement;
    if (tgt.closest('.canvas-node')) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    isPanningRef.current = true;
    panStartRef.current = { x: e.clientX, y: e.clientY };
    panOriginRef.current = { ...offset };
  };

  const onBackgroundPointerMove: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (!isPanningRef.current) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    setView((prev) => ({
      ...prev,
      offset: { x: panOriginRef.current.x + dx, y: panOriginRef.current.y + dy },
    }));
  };

  const onBackgroundPointerUp: React.PointerEventHandler<HTMLDivElement> = (e) => {
    isPanningRef.current = false;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
  };

  // Wheel to zoom
  const handleWheel = useCallback((event: WheelEvent) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;
    event.preventDefault();
    const pointX = event.clientX - rect.left;
    const pointY = event.clientY - rect.top;
    const zoomIntensity = 0.0015;
    const factor = Math.exp(-event.deltaY * zoomIntensity);
    setView((prev) => {
      const newScale = clamp(prev.scale * factor, 0.3, 2.0);
      const worldX = (pointX - prev.offset.x) / prev.scale;
      const worldY = (pointY - prev.offset.y) / prev.scale;
      return {
        scale: newScale,
        offset: {
          x: pointX - worldX * newScale,
          y: pointY - worldY * newScale,
        },
      };
    });
  }, []);

  useEffect(() => {
    if (!isOpen || isMinimized) return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      viewport.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel, isMinimized, isOpen]);

  const zoomAtViewportPoint = useCallback((pointX: number, pointY: number, scaleUpdater: (currentScale: number) => number) => {
    setView((prev) => {
      const newScale = clamp(scaleUpdater(prev.scale), 0.3, 2.0);
      const worldX = (pointX - prev.offset.x) / prev.scale;
      const worldY = (pointY - prev.offset.y) / prev.scale;
      return {
        scale: newScale,
        offset: {
          x: pointX - worldX * newScale,
          y: pointY - worldY * newScale,
        },
      };
    });
  }, []);

  const zoomIn = () => {
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;
    zoomAtViewportPoint(rect.width / 2, rect.height / 2, (currentScale) => currentScale * 1.1);
  };

  const zoomOut = () => {
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;
    zoomAtViewportPoint(rect.width / 2, rect.height / 2, (currentScale) => currentScale / 1.1);
  };

  const resetView = () => {
    setView({ scale: 1, offset: { x: 0, y: 0 } });
  };

  const fitToContent = () => {
    if (nodes.length === 0 || !viewportRef.current) return resetView();
    const minX = Math.min(...nodes.map((n) => n.x));
    const minY = Math.min(...nodes.map((n) => n.y));
    const maxX = Math.max(...nodes.map((n) => n.x + n.width));
    const maxY = Math.max(...nodes.map((n) => n.y + n.height));
    const width = maxX - minX;
    const height = maxY - minY;

    const vp = viewportRef.current.getBoundingClientRect();
    const pad = 40;
    const sx = (vp.width - pad) / width;
    const sy = (vp.height - pad) / height;
    const newScale = clamp(Math.min(sx, sy), 0.3, 2.0);

    const worldCenterX = (minX + maxX) / 2;
    const worldCenterY = (minY + maxY) / 2;
    const viewportCenterX = vp.width / 2;
    const viewportCenterY = vp.height / 2;

    setView({
      scale: newScale,
      offset: {
        x: viewportCenterX - worldCenterX * newScale,
        y: viewportCenterY - worldCenterY * newScale,
      },
    });
  };

  // Node drag
  const draggingRef = useRef<{ id: number; startX: number; startY: number; nodeStartX: number; nodeStartY: number } | null>(null);

  const onNodePointerDown = (id: number): React.PointerEventHandler<HTMLDivElement> => (e) => {
    // Do not start drag while editing this node
    if (editingBodyId === id) return;
    // Ignore if starting on resize handle
    const tgt = e.target as HTMLElement;
    if (tgt.closest('.canvas-node-resize')) return;
    e.stopPropagation();
    const { x: sx, y: sy } = toWorld(e.clientX, e.clientY);
    const node = nodes.find((n) => n.id === id)!;
    draggingRef.current = { id, startX: sx, startY: sy, nodeStartX: node.x, nodeStartY: node.y };
  };

  const onNodePointerUp: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (draggingRef.current) {
      draggingRef.current = null;
    }
  };

  // Node resize
  const resizingRef = useRef<{ id: number; startX: number; startY: number; startW: number; startH: number } | null>(null);

  const onResizePointerDown = (id: number): React.PointerEventHandler<HTMLDivElement> => (e) => {
    e.stopPropagation();
    const { x, y } = toWorld(e.clientX, e.clientY);
    const node = nodes.find((n) => n.id === id)!;
    resizingRef.current = { id, startX: x, startY: y, startW: node.width, startH: node.height };
  };

  const onViewportPointerMove: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (resizingRef.current) {
      const { id, startX, startY, startW, startH } = resizingRef.current;
      const { x, y } = toWorld(e.clientX, e.clientY);
      const dx = x - startX;
      const dy = y - startY;
      setNodes((prev) => prev.map((n) => (
        n.id === id
          ? { ...n, width: Math.max(MIN_NODE_WIDTH, startW + dx), height: Math.max(MIN_NODE_HEIGHT, startH + dy) }
          : n
      )));
      return;
    }
    if (draggingRef.current) {
      const { id, startX, startY, nodeStartX, nodeStartY } = draggingRef.current;
      const { x, y } = toWorld(e.clientX, e.clientY);
      const dx = x - startX;
      const dy = y - startY;
      setNodes((prev) => prev.map((n) => (n.id === id ? { ...n, x: nodeStartX + dx, y: nodeStartY + dy } : n)));
      return;
    }
    if (isPanningRef.current) {
      onBackgroundPointerMove(e);
    }
  };

  const onViewportPointerUp: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (resizingRef.current) {
      resizingRef.current = null;
    }
    if (draggingRef.current) {
      draggingRef.current = null;
    }
    if (isPanningRef.current) onBackgroundPointerUp(e);
  };

  const addNote = () => {
    const center = toWorld(
      (viewportRef.current?.getBoundingClientRect().left || 0) + (viewportRef.current?.clientWidth || 0) / 2,
      (viewportRef.current?.getBoundingClientRect().top || 0) + (viewportRef.current?.clientHeight || 0) / 2
    );
    const id = Date.now();
    setNodes((prev) => [
      ...prev,
      { id, x: center.x - DEFAULT_NODE_WIDTH / 2, y: center.y - DEFAULT_NODE_HEIGHT / 2, width: DEFAULT_NODE_WIDTH, height: DEFAULT_NODE_HEIGHT, body: 'New note', category: 'Notes' },
    ]);
    onAddCategory && onAddCategory('Notes');
  };

  // Insert SWOT template (four nodes in 2x2 grid around center)
  const insertSwotTemplate = () => {
    if (!viewportRef.current) return;
    const vpRect = viewportRef.current.getBoundingClientRect();
    const center = toWorld(vpRect.left + vpRect.width / 2, vpRect.top + vpRect.height / 2);

    const tileW = DEFAULT_NODE_WIDTH;
    const tileH = DEFAULT_NODE_HEIGHT;
    const gap = 40;

    const base = Date.now();
    const strengths: NodeState = { id: base + 1, x: center.x - tileW - gap / 2, y: center.y - tileH - gap / 2, width: tileW, height: tileH, body: '<ul><li>Add strength</li></ul>', category: 'Strengths' };
    const weaknesses: NodeState = { id: base + 2, x: center.x + gap / 2, y: center.y - tileH - gap / 2, width: tileW, height: tileH, body: '<ul><li>Add weakness</li></ul>', category: 'Weaknesses' };
    const opportunities: NodeState = { id: base + 3, x: center.x - tileW - gap / 2, y: center.y + gap / 2, width: tileW, height: tileH, body: '<ul><li>Add opportunity</li></ul>', category: 'Opportunities' };
    const threats: NodeState = { id: base + 4, x: center.x + gap / 2, y: center.y + gap / 2, width: tileW, height: tileH, body: '<ul><li>Add threat</li></ul>', category: 'Threats' };

    setNodes((prev) => [...prev, strengths, weaknesses, opportunities, threats]);
    // Add categories on separate ticks to avoid React batching dropping intermediate updates
    if (onAddCategory) {
      const cats = ['Strengths', 'Weaknesses', 'Opportunities', 'Threats'];
      cats.forEach((cat, idx) => {
        setTimeout(() => onAddCategory(cat), idx * 0);
      });
    }
    setShowTemplates(false);
  };

  // Insert 2x2 Positioning Matrix (single resizable node with editable labels)
  const insertPositioningTemplate = () => {
    if (!viewportRef.current) return;
    const vpRect = viewportRef.current.getBoundingClientRect();
    const center = toWorld(vpRect.left + vpRect.width / 2, vpRect.top + vpRect.height / 2);
    const nodeWidth = 560;
    const nodeHeight = 400;
    const id = Date.now();
    const matrixDefaults: MatrixState = {
      xMin: 'Low X',
      xMax: 'High X',
      yMin: 'Low Y',
      yMax: 'High Y',
      qTL: 'Top-Left',
      qTR: 'Top-Right',
      qBL: 'Bottom-Left',
      qBR: 'Bottom-Right',
    };
    const node: NodeState = {
      id,
      x: center.x - nodeWidth / 2,
      y: center.y - nodeHeight / 2,
      width: nodeWidth,
      height: nodeHeight,
      body: '',
      category: 'Positioning',
      kind: 'matrix',
      matrix: matrixDefaults,
    };
    setNodes((prev) => [...prev, node]);
    onAddCategory && onAddCategory('Positioning');
    setShowTemplates(false);
  };

  // Insert Table template (user specifies rows/cols; first row/col are titles)
  const insertTableTemplate = () => {
    if (!viewportRef.current) return;
    let rowsInput = window.prompt('Number of rows (min 2):', '4');
    if (rowsInput === null) return; // cancelled
    let colsInput = window.prompt('Number of columns (min 2):', '4');
    if (colsInput === null) return; // cancelled
    let rows = Math.max(2, Math.min(50, parseInt(rowsInput as string, 10) || 2));
    let cols = Math.max(2, Math.min(50, parseInt(colsInput as string, 10) || 2));

    const vpRect = viewportRef.current.getBoundingClientRect();
    const center = toWorld(vpRect.left + vpRect.width / 2, vpRect.top + vpRect.height / 2);
    const cellW = 120;
    const cellH = 42;
    const nodeWidth = Math.max(DEFAULT_NODE_WIDTH, Math.min(1400, cols * cellW + 40));
    const nodeHeight = Math.max(DEFAULT_NODE_HEIGHT, Math.min(1000, rows * cellH + 80));

    const id = Date.now();
    const data: string[][] = Array.from({ length: rows }, () => Array.from({ length: cols }, () => ''));
    const node: NodeState = {
      id,
      x: center.x - nodeWidth / 2,
      y: center.y - nodeHeight / 2,
      width: nodeWidth,
      height: nodeHeight,
      body: '',
      category: 'Table',
      kind: 'table',
      table: { rows, cols, data },
    };
    setNodes((prev) => [...prev, node]);
    onAddCategory && onAddCategory('Table');
    setShowTemplates(false);
  };

  // Body editing handlers
  const startEditBody = (id: number) => setEditingBodyId(id);
  const stopEditing = () => setEditingBodyId(null);

  const commitBody = (id: number, el: HTMLDivElement) => {
    const html = el.innerHTML || '';
    setNodes((prev) => prev.map((n) => (n.id === id ? { ...n, body: html } : n)));
  };

  // (pendingAdd prop no longer used; events handle routing)

  if (!isOpen) return null;

  if (isMinimized) {
    return (
      <div className="canvas-minimized" role="button" onClick={() => setIsMinimized(false)} title="Restore canvas">
        <span className="canvas-minimized-label">Canvas</span>
        <span className="canvas-minimized-count">{nodes.length}</span>
        <button
          className="canvas-minimized-close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close canvas"
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <div className="canvas-modal-overlay" role="dialog" aria-modal="true">
      <div className="canvas-modal">
        <div className="canvas-toolbar">
          <div className="canvas-toolbar-left">
            <button className="canvas-btn" onClick={zoomOut} title="Zoom out">−</button>
            <button className="canvas-btn" onClick={resetView} title="Reset view">Reset</button>
            <button className="canvas-btn" onClick={zoomIn} title="Zoom in">+</button>
            <span className="canvas-divider" />
            <button className="canvas-btn" onClick={fitToContent} title="Fit to content">Fit</button>
            <span className="canvas-divider" />
            <button className="canvas-btn" onClick={addNote} title="Add note">Add Note</button>
            <div className="canvas-template-wrap">
              <button className="canvas-btn" onClick={() => setShowTemplates((s) => !s)} title="Templates">Templates ▾</button>
              {showTemplates && (
                <div className="canvas-template-menu">
                  <div className="canvas-template-item" onClick={insertSwotTemplate}>SWOT</div>
                    <div className="canvas-template-item" onClick={insertPositioningTemplate}>Positioning Matrix (2×2)</div>
                    <div className="canvas-template-item" onClick={insertTableTemplate}>Table…</div>
                </div>
              )}
            </div>
          </div>
          <div className="canvas-toolbar-right">
            <button className="canvas-btn" onClick={() => setIsMinimized(true)} title="Minimize">Minimize</button>
            <span className="canvas-zoom-label">{Math.round(scale * 100)}%</span>
            <button className="canvas-close" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>
        <div
          ref={viewportRef}
          className="canvas-viewport"
          onPointerDown={onBackgroundPointerDown}
          onPointerMove={onViewportPointerMove}
          onPointerUp={onViewportPointerUp}
        >
          <div
            className="canvas-world"
            style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
          >
            <div className="canvas-grid" />
            {nodes.map((n) => (
              <div
                key={n.id}
                data-node-id={n.id}
                className={`canvas-node${editingBodyId === n.id ? ' canvas-node-editing' : ''}`}
                style={{ left: `${n.x}px`, top: `${n.y}px`, width: `${n.width}px`, height: `${n.height}px` }}
                onPointerDown={onNodePointerDown(n.id)}
                onPointerUp={onNodePointerUp}
              >
                {n.category && (
                  <div className="canvas-node-cat">{n.category}</div>
                )}

                {n.kind === 'matrix' && n.matrix ? (
                  <div className="matrix-container">
                    {/* grid lines */}
                    <div className="matrix-line matrix-line-h" />
                    <div className="matrix-line matrix-line-v" />

                    {/* quadrant labels */}
                    <div
                      className="matrix-q matrix-q-tl"
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => {
                        const text = e.currentTarget.innerText.trim();
                        setNodes((prev) => prev.map((m) => (m.id === n.id ? { ...m, matrix: { ...(m.matrix as MatrixState), qTL: text || (m.matrix as MatrixState).qTL } } : m)));
                      }}
                    >{n.matrix.qTL}</div>
                    <div
                      className="matrix-q matrix-q-tr"
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => {
                        const text = e.currentTarget.innerText.trim();
                        setNodes((prev) => prev.map((m) => (m.id === n.id ? { ...m, matrix: { ...(m.matrix as MatrixState), qTR: text || (m.matrix as MatrixState).qTR } } : m)));
                      }}
                    >{n.matrix.qTR}</div>
                    <div
                      className="matrix-q matrix-q-bl"
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => {
                        const text = e.currentTarget.innerText.trim();
                        setNodes((prev) => prev.map((m) => (m.id === n.id ? { ...m, matrix: { ...(m.matrix as MatrixState), qBL: text || (m.matrix as MatrixState).qBL } } : m)));
                      }}
                    >{n.matrix.qBL}</div>
                    <div
                      className="matrix-q matrix-q-br"
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => {
                        const text = e.currentTarget.innerText.trim();
                        setNodes((prev) => prev.map((m) => (m.id === n.id ? { ...m, matrix: { ...(m.matrix as MatrixState), qBR: text || (m.matrix as MatrixState).qBR } } : m)));
                      }}
                    >{n.matrix.qBR}</div>

                    {/* axis labels */}
                    <div className="matrix-axis matrix-axis-x">
                      <span
                        className="matrix-axis-end matrix-axis-x-left"
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          const text = e.currentTarget.innerText.trim();
                          setNodes((prev) => prev.map((m) => (m.id === n.id ? { ...m, matrix: { ...(m.matrix as MatrixState), xMin: text || (m.matrix as MatrixState).xMin } } : m)));
                        }}
                      >{n.matrix.xMin}</span>
                      <span
                        className="matrix-axis-end matrix-axis-x-right"
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          const text = e.currentTarget.innerText.trim();
                          setNodes((prev) => prev.map((m) => (m.id === n.id ? { ...m, matrix: { ...(m.matrix as MatrixState), xMax: text || (m.matrix as MatrixState).xMax } } : m)));
                        }}
                      >{n.matrix.xMax}</span>
                    </div>
                    <div className="matrix-axis matrix-axis-y">
                      <span
                        className="matrix-axis-end matrix-axis-y-bottom"
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          const text = e.currentTarget.innerText.trim();
                          setNodes((prev) => prev.map((m) => (m.id === n.id ? { ...m, matrix: { ...(m.matrix as MatrixState), yMin: text || (m.matrix as MatrixState).yMin } } : m)));
                        }}
                      >{n.matrix.yMin}</span>
                      <span
                        className="matrix-axis-end matrix-axis-y-top"
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          const text = e.currentTarget.innerText.trim();
                          setNodes((prev) => prev.map((m) => (m.id === n.id ? { ...m, matrix: { ...(m.matrix as MatrixState), yMax: text || (m.matrix as MatrixState).yMax } } : m)));
                        }}
                      >{n.matrix.yMax}</span>
                    </div>
                  </div>
                ) : n.kind === 'table' && n.table ? (
                  <div className="table-container" onPointerDown={(e) => e.stopPropagation()}>
                    <div className="table-toolbar">
                      <button
                        className="table-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setNodes((prev) => prev.map((m) => {
                            if (m.id !== n.id || !m.table) return m;
                            const cols = m.table.cols;
                            const newRow = Array.from({ length: cols }, () => '');
                            return { ...m, table: { rows: m.table.rows + 1, cols, data: [...m.table.data, newRow] } };
                          }));
                        }}
                        title="Add row"
                      >+ Row</button>
                      <button
                        className="table-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setNodes((prev) => prev.map((m) => {
                            if (m.id !== n.id || !m.table) return m;
                            if (m.table.rows <= 2) return m;
                            const nextData = m.table.data.slice(0, m.table.rows - 1);
                            return { ...m, table: { rows: m.table.rows - 1, cols: m.table.cols, data: nextData } };
                          }));
                        }}
                        title="Remove last row"
                      >− Row</button>
                      <span className="table-divider" />
                      <button
                        className="table-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setNodes((prev) => prev.map((m) => {
                            if (m.id !== n.id || !m.table) return m;
                            const rows = m.table.rows;
                            const nextData = m.table.data.map((r) => [...r, '']);
                            return { ...m, table: { rows, cols: m.table.cols + 1, data: nextData } };
                          }));
                        }}
                        title="Add column"
                      >+ Col</button>
                      <button
                        className="table-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setNodes((prev) => prev.map((m) => {
                            if (m.id !== n.id || !m.table) return m;
                            const { rows, cols, data } = m.table;
                            if (cols <= 2) return m;
                            const nextData = data.map((r) => r.slice(0, cols - 1));
                            return { ...m, table: { rows, cols: cols - 1, data: nextData } };
                          }));
                        }}
                        title="Remove last column"
                      >− Col</button>
                    </div>
                    <div className="table-scroll">
                      <table className="canvas-table" onMouseDown={(e) => e.stopPropagation()}>
                        <tbody>
                          {Array.from({ length: (n.table ? n.table.rows : 0) }).map((_, rIdx) => (
                            <tr key={rIdx}>
                              {Array.from({ length: (n.table ? n.table.cols : 0) }).map((_, cIdx) => {
                                const isHeader = rIdx === 0 || cIdx === 0;
                                return (
                                  <td key={cIdx} className={isHeader ? 'table-cell table-cell-header' : 'table-cell'}>
                                    <div
                                      className="table-cell-editor"
                                      contentEditable
                                      suppressContentEditableWarning
                                      onPointerDown={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        if (!isHeader && targetingContent) {
                                          e.stopPropagation();
                                          const content = targetingContent;
                                          setNodes((prev) => prev.map((m) => {
                                            if (m.id !== n.id || !m.table) return m;
                                            const nextData = m.table.data.map((row, rr) => row.map((val, cc) => (rr === rIdx && cc === cIdx ? String(content) : val)));
                                            return { ...m, table: { rows: m.table.rows, cols: m.table.cols, data: nextData } };
                                          }));
                                          setTargetingContent(null);
                                          window.dispatchEvent(new Event('canvas-targeting-complete'));
                                        }
                                      }}
                                      onBlur={(e) => {
                                        const text = e.currentTarget.innerText;
                                        setNodes((prev) => prev.map((m) => {
                                          if (m.id !== n.id || !m.table) return m;
                                          const nextData = m.table.data.map((row, rr) => row.map((val, cc) => (rr === rIdx && cc === cIdx ? text : val)));
                                          return { ...m, table: { rows: m.table.rows, cols: m.table.cols, data: nextData } };
                                        }));
                                      }}
                                    >{n.table ? n.table.data[rIdx][cIdx] : ''}</div>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div
                    className="canvas-node-text"
                    onDoubleClick={() => startEditBody(n.id)}
                  >
                    <div
                      contentEditable={editingBodyId === n.id}
                      suppressContentEditableWarning
                      onPointerDown={(e) => e.stopPropagation()}
                      onBlur={(e) => { commitBody(n.id, e.currentTarget); stopEditing(); }}
                      dangerouslySetInnerHTML={{ __html: n.body }}
                    />
                  </div>
                )}

                <div className="canvas-node-resize" onPointerDown={onResizePointerDown(n.id)} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfiniteCanvasModal;


